import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkDirective from 'remark-directive';
import rehypeSlug from 'rehype-slug';
import type { Components } from 'react-markdown';
import { Link } from '@tanstack/react-router';
import remarkDocTokens from './remarkDocTokens';
import remarkDocCallouts from './remarkDocCallouts';
import KindleEmailStep from './KindleEmailStep';
import SaveEmailIntro from './SaveEmailIntro';
import FolderAddressesSection from './FolderAddressesSection';
import BrandAssetsSection from './BrandAssetsSection';
import DocCallout from './DocCallout';

interface DocsArticleProps {
  content: string;
  className?: string;
}

const components: Components = {
  h2: ({ children, ...props }) => (
    <h2 className="docs-heading-2" {...props}>
      {children}
    </h2>
  ),
  h3: ({ children, ...props }) => (
    <h3 className="docs-heading-3" {...props}>
      {children}
    </h3>
  ),
  p: ({ children, ...props }) => (
    <p className="docs-paragraph" {...props}>
      {children}
    </p>
  ),
  ul: ({ children, ...props }) => (
    <ul className="docs-list" {...props}>
      {children}
    </ul>
  ),
  ol: ({ children, ...props }) => (
    <ol className="docs-list docs-list-ordered" {...props}>
      {children}
    </ol>
  ),
  li: ({ children, ...props }) => (
    <li className="docs-list-item" {...props}>
      {children}
    </li>
  ),
  a: ({ children, href, ...props }) => {
    // Internal links (e.g. "/docs/reader/notes" or "/docs/speak/overview#voices")
    // are handled by TanStack Router for client-side navigation instead of a
    // full page reload. Everything else (http(s), mailto:, in-page #hash) stays
    // a plain anchor.
    const isInternal = href?.startsWith('/');

    if (isInternal && href) {
      const [pathname, hash] = href.split('#');
      return (
        <Link to={pathname} hash={hash} className="docs-link" {...props}>
          {children}
        </Link>
      );
    }

    const isExternal = href?.startsWith('http');
    return (
      <a
        href={href}
        className="docs-link"
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        {...props}
      >
        {children}
      </a>
    );
  },
  code: ({ children, className, ...props }) => {
    const isBlock = className?.startsWith('language-');
    if (isBlock) {
      return (
        <code className={`docs-code-block ${className ?? ''}`} {...props}>
          {children}
        </code>
      );
    }
    return (
      <code className="docs-code-inline" {...props}>
        {children}
      </code>
    );
  },
  pre: ({ children, ...props }) => (
    <pre className="docs-pre" {...props}>
      {children}
    </pre>
  ),
  table: ({ children, ...props }) => (
    <div className="docs-table-wrapper">
      <table className="docs-table" {...props}>
        {children}
      </table>
    </div>
  ),
  th: ({ children, ...props }) => (
    <th className="docs-table-header" {...props}>
      {children}
    </th>
  ),
  td: ({ children, ...props }) => (
    <td className="docs-table-cell" {...props}>
      {children}
    </td>
  ),
  blockquote: ({ children, ...props }) => (
    <blockquote className="docs-blockquote" {...props}>
      {children}
    </blockquote>
  ),
  hr: (props) => <hr className="docs-hr" {...props} />,
  strong: ({ children, ...props }) => (
    <strong className="docs-strong" {...props}>
      {children}
    </strong>
  ),
};

// These are custom elements emitted by remarkDocTokens. They aren't part of
// react-markdown's intrinsic-element Components map, so we attach them via a cast.
const componentsWithTokens = {
  ...components,
  'kindle-email-step': () => <KindleEmailStep />,
  'save-email-intro': () => <SaveEmailIntro />,
  'folder-emails': () => <FolderAddressesSection />,
  'press-brand-assets': () => <BrandAssetsSection />,
  'doc-callout': DocCallout,
} as Components;

function DocsArticle({ content, className }: DocsArticleProps) {
  return (
    <article className={`docs-article ${className ?? ''}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkDirective, remarkDocCallouts, remarkDocTokens]}
        rehypePlugins={[rehypeSlug]}
        components={componentsWithTokens}
      >
        {content}
      </ReactMarkdown>
    </article>
  );
}

export default DocsArticle;
